// ============================================================
// 開発用: 東京駅固定 + (オプション) 歩行シミュレーション
// ============================================================

import type { Fix, LatLon } from '../types'
import type { LocationProvider } from './location'
import { toRad } from '../util/distance'

export interface MockWalkConfig {
  /** 進行方位 (true north 度) */
  bearing: number
  /** 速度 (m/s) — 早歩き 1.4 推奨 */
  speed: number
}

export interface MockLocationOptions {
  start: LatLon
  walk: MockWalkConfig | null
  /** Tick 周期 (ms)。default 100ms */
  tickMs?: number
  /** GPS ノイズ (m)。default 5m。0 でノイズなし */
  noiseM?: number
}

export class MockLocationProvider implements LocationProvider {
  private fix: Fix | null = null
  private handlers = new Set<(f: Fix) => void>()
  private timer: ReturnType<typeof setInterval> | null = null
  private startedAt = 0

  constructor(private opts: MockLocationOptions) {}

  async start(): Promise<void> {
    if (this.timer) return
    this.startedAt = Date.now()
    this.fix = this.makeFix(this.opts.start, 0)
    this.emit()

    const tickMs = this.opts.tickMs ?? 100
    this.timer = setInterval(() => this.tick(), tickMs)
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  current(): Fix | null {
    return this.fix
  }

  subscribe(handler: (f: Fix) => void): () => void {
    this.handlers.add(handler)
    if (this.fix) handler(this.fix)
    return () => this.handlers.delete(handler)
  }

  /** ランタイムで歩行モードを切り替える (Vite HMR から呼ぶ用) */
  setWalk(walk: MockWalkConfig | null): void {
    this.opts.walk = walk
  }

  /** ランタイムで位置を指定 (テスト用) */
  jumpTo(latlon: LatLon): void {
    this.opts.start = latlon
    this.startedAt = Date.now()
    this.fix = this.makeFix(latlon, 0)
    this.emit()
  }

  // ───────── private ─────────

  private tick(): void {
    const elapsedSec = (Date.now() - this.startedAt) / 1000
    const next = this.computePosition(elapsedSec)
    this.fix = this.makeFix(next, elapsedSec)
    this.emit()
  }

  private computePosition(elapsedSec: number): LatLon {
    const { start, walk } = this.opts
    if (!walk) return start

    // 歩行: 指定方位に v * t メートル進む
    const distM = walk.speed * elapsedSec
    const θ = toRad(walk.bearing)
    const dx = distM * Math.sin(θ)             // 東方向 m
    const dy = distM * Math.cos(θ)             // 北方向 m
    const lat = start.lat + dy / 111_320
    const lon = start.lon + dx / (111_320 * Math.cos(toRad(start.lat)))
    return { lat, lon }
  }

  private makeFix(p: LatLon, elapsedSec: number): Fix {
    const noiseM = this.opts.noiseM ?? 5
    const jLat = noiseM > 0 ? ((Math.random() - 0.5) * noiseM) / 111_320 : 0
    const jLon = noiseM > 0 ? ((Math.random() - 0.5) * noiseM) / 111_320 : 0
    return {
      lat: p.lat + jLat,
      lon: p.lon + jLon,
      accuracy: Math.max(3, noiseM),
      speed: this.opts.walk?.speed ?? null,
      heading: this.opts.walk?.bearing ?? null,
      timestamp: this.startedAt + elapsedSec * 1000,
    }
  }

  private emit(): void {
    if (!this.fix) return
    const f = this.fix
    this.handlers.forEach((h) => h(f))
  }
}
